import { useEffect, useState } from 'react';
import type { FleetClient, RepairInfo } from '@galaxis/shared';
import { useGameStore } from '../../store/game-store.ts';
import { fetchRepairInfo, startRepair, fetchFleets, fetchResources } from '../../api/galaxy.ts';
import { formatDuration, newIdempotencyKey, repairView } from './fleet-format.ts';
import { useNow } from './FleetIcons.tsx';

/**
 * M10.11: sérült hajók javítása (idő + fém). Csak akkor jelenik meg, ha a flottában van sérült hajó;
 * a szerver dönti el, hogy most javítható-e (szabad flotta, saját rendszerben), a panel az indokot mutatja.
 */
export function RepairPanel({ fleet }: { fleet: FleetClient }) {
  const { setFleets, setResources } = useGameStore();
  const [info, setInfo] = useState<RepairInfo | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const now = useNow();

  useEffect(() => {
    let alive = true;
    fetchRepairInfo(fleet.id).then(i => { if (alive) setInfo(i); }).catch(() => { if (alive) setInfo(null); });
    return () => { alive = false; };
  }, [fleet.id, fleet.status, fleet.currentSystemId, fleet.stats.damagedShips]);

  const view = repairView(info, fleet.stats.damagedShips, now);
  if (view.kind === 'none') return null;

  const start = async () => {
    setBusy(true); setError(null);
    try {
      await startRepair(fleet.id, newIdempotencyKey('repair'));
      const [fleets, res, fresh] = await Promise.all([fetchFleets(), fetchResources(), fetchRepairInfo(fleet.id)]);
      setFleets(fleets.fleets); setResources(res); setInfo(fresh);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'A javítás nem indítható.');
    } finally { setBusy(false); }
  };

  return (
    <section className="sci-fi-panel rounded-xl p-4 space-y-2" aria-label="Javítás" data-testid="repair-panel">
      <h3 className="text-xs text-slate-500 uppercase tracking-wide">Javítás</h3>
      {view.kind === 'open' ? (
        <p className="text-sm text-emerald-300" data-testid="repair-progress" role="status">
          🔧 Javítás folyamatban: {formatDuration(view.remainingMs)} hátravan · {view.ships} hajó. A flotta addig maradjon a rendszerben.
        </p>
      ) : (
        <>
          <p className="text-sm text-slate-200">
            {view.ships} sérült hajó · <span className="font-mono text-amber-300">{view.metal} fém</span> · {formatDuration(view.timeMs)}
          </p>
          <button type="button" disabled={!view.canStart || busy} title={view.reason ?? undefined} data-testid="repair-start"
            className="rounded-lg text-sm font-medium px-3 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
            style={{ minHeight: 44, background: '#34d39914', color: '#34d399', border: '1px solid #34d39955' }} onClick={start}>
            🔧 Javítás indítása
          </button>
          {view.reason && <p className="text-xs text-slate-500" data-testid="repair-reason">{view.reason}</p>}
        </>
      )}
      {error && <p className="text-xs text-red-300" role="alert">{error}</p>}
    </section>
  );
}
